/**
 * Routing helpers for task notifications.
 *
 * The notification body may arrive as a plain string (legacy / simple kinds)
 * or as a JSON-encoded object (cross-org DRAFT_* kinds denormalize routing
 * hints into the body — `target_head_office_id`, `task_id`, `task_title`).
 *
 * `destinationFor` decides where a notification click should navigate:
 *   - DRAFT_RECEIVED / DRAFT_RESUBMITTED → admin's Draft Tasks tab
 *   - DRAFT_SUBMITTED / DRAFT_APPROVED   → originator's Partner Task page
 *   - DRAFT_SEND_BACK / DRAFT_REJECTED   → originator's Partner Task page,
 *     deep-linked to the Activity tab so the reason is immediately visible
 *   - everything else → /tasks/{task_id} (existing behaviour)
 */

export function parseNotificationBody(
  body: string | null | undefined,
): Record<string, any> {
  if (!body) return {};
  if (typeof body !== "string") return body as any;
  try {
    const parsed = JSON.parse(body);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

export function destinationFor(
  kind: string,
  taskId: string,
  _bodyStr?: string | null,
): string {
  // Daily-planner (todo) notifications ride the bell with a namespaced
  // task_id of "todo:<id>" (they have no real task). Route them to the
  // planner instead of /tasks/<id>.
  if (taskId && taskId.startsWith("todo:")) return "/todo";
  // Leave (apply/approve/reject) notifications ride the bell with a
  // namespaced task_id of "leave:<id>" — route them to the Leaves page.
  if (taskId && taskId.startsWith("leave:")) return "/leaves";
  // Expense (submit/approve/reject/reimburse) notifications ride the bell with
  // a namespaced task_id of "expense:<report_id>" — route them to /expenses.
  if (taskId && taskId.startsWith("expense:")) return "/expenses";
  // Feedback & Complaint notifications ride the bell with a namespaced task_id
  // of "grievance:<id>" — route them to the Feedback & Complaints panel.
  if (taskId && taskId.startsWith("grievance:")) return "/grievances";
  // A Lio question the superadmin assigned to me: task_id
  // "lio_issue:<issue_id>". There is no per-issue page — the list is short and
  // the whole row renders in it — so the click lands on the list.
  if (taskId && taskId.startsWith("lio_issue:")) return "/my-lio-issues";
  // Salary (generated) notifications ride the bell with a namespaced task_id
  // of "salary:<year>-<mm>" — route them to the Documents page.
  if (taskId && taskId.startsWith("salary:")) return "/documents";
  // Asset (assign/accept) notifications ride the bell with a namespaced task_id
  // of "asset:<asset_id>" — route them to the Asset Management page.
  if (taskId && taskId.startsWith("asset:")) return "/assets";
  // Visitor (invited/arrived/checked-out/cancelled) notifications ride the
  // bell with a namespaced task_id of "visitor:<visitor_id>" — route them to
  // the Visitor Management page.
  if (taskId && taskId.startsWith("visitor:")) return "/visitors";
  // A note shared with me: task_id "note:<note_id>" — open that note in
  // Notemaker (it lists under "Shared with me", read-only).
  if (taskId && taskId.startsWith("note:")) return `/notes/${encodeURIComponent(taskId.slice("note:".length))}`;
  // A meeting shared with me: task_id "meeting:<meeting_id>" — open it
  // (read-only; it also lists under Meeting → Shared with me).
  if (taskId && taskId.startsWith("meeting:")) return `/meetly/meetings/${encodeURIComponent(taskId.slice("meeting:".length))}`;
  // Resignations ride the bell as "separation:<resignation_id>", but the two
  // audiences have different pages: the employee's own resignation lives at
  // /my-resignation, the approval queue at /separation/resignations. The id
  // cannot tell them apart, so the KIND picks the page.
  if (taskId && taskId.startsWith("separation:")) {
    return kind === "SEPARATION_REQUEST" || kind === "SEPARATION_WITHDRAWN"
      ? "/separation/resignations"
      : "/my-resignation";
  }

  // Attendance (late-coming warning) notifications ride the bell with a
  // namespaced task_id of "attendance:<date>" — route them to /attendance so
  // the employee lands on the punches the warning is about.
  if (taskId && taskId.startsWith("attendance:")) return "/attendance";
  // Announcement notices (an upcoming birthday) ride the bell with a namespaced
  // task_id of "announcement:<id>". The notice itself scrolls in the header on
  // every page, so the click simply lands on the dashboard.
  if (taskId && taskId.startsWith("announcement:")) {
    // Probation reminders share the announcement feed but are an action item
    // for the manager/HR, not a notice to read: send them to the employee list
    // where the record can actually be changed.
    return kind === "PROBATION_ENDING" ? "/employees" : "/dashboard";
  }
  switch (kind) {
    case "DRAFT_RECEIVED":
    case "DRAFT_RESUBMITTED":
      return `/tasks/list?scope=drafts&review=${encodeURIComponent(taskId)}`;
    case "DRAFT_SUBMITTED":
    case "DRAFT_APPROVED":
      return `/partner-task/${encodeURIComponent(taskId)}`;
    case "DRAFT_SEND_BACK":
    case "DRAFT_REJECTED":
      return `/partner-task/${encodeURIComponent(taskId)}?tab=activity`;
    // Client/partner task kinds — recipient is a linked partner user; route
    // them to the partner task detail page (consolidated for messenger,
    // which does not use the /client-portal page).
    case "CLIENT_TASK_CREATED":
    case "CLIENT_TASK_STATUS":
      return `/partner-task/${encodeURIComponent(taskId)}`;
    case "MENTIONED":
    case "COMMENT":
      return `/tasks/${encodeURIComponent(taskId)}?tab=comments`;
    case "CHECKLIST":
      return `/tasks/${encodeURIComponent(taskId)}?tab=checklist`;
    default:
      return `/tasks/${taskId}`;
  }
}
